import 'react-datepicker/dist/react-datepicker.css';

import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/react';
import { MouseEventHandler, ReactElement, ReactNode, forwardRef } from 'react';
import DatePicker from 'react-datepicker';

import { radius } from 'styles/revampTokens';

import datePickerStyle from '../../ModelOverviewPage/style';

interface Properties {
  value?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  planPermission?: boolean;
  styles?: React.CSSProperties;
  leftIcon?: ReactElement;
  hideChevron?: boolean;
  placeholderLabel?: string;
  filledColor?: string;
  emptyColor?: string;
}

export const ModelSettingsDatePickerInput = forwardRef<HTMLButtonElement, Properties>(
  (
    {
      value,
      onClick,
      planPermission,
      styles,
      leftIcon,
      hideChevron,
      placeholderLabel,
      filledColor,
      emptyColor,
    },
    reference,
  ) => {
    const handleClick = (event: any) => {
      event.preventDefault();
      if (onClick) onClick(event);
    };

    const hasValue = Boolean(value);

    return (
      <Button
        ref={reference}
        cursor={planPermission ? 'not-allowed' : 'pointer'}
        onClick={(event: any) => (planPermission ? undefined : handleClick(event))}
        rightIcon={hideChevron ? undefined : <ChevronDownIcon boxSize={4} color='gray.500' />}
        leftIcon={leftIcon}
        width={'100px'}
        height={'30px'}
        variant='picker'
        color={hasValue ? filledColor || 'gray.700' : emptyColor || '#BEBEBE'}
        fontSize='xs'
        fontWeight='500'
        borderRadius={radius.overlay}
        border='1px solid #D1D6DD'
        style={styles}
      >
        {hasValue ? value : placeholderLabel || ''}
      </Button>
    );
  },
);

const ModelSettingsDatePicker = ({
  selected,
  onChange,
  placeholder,
  label,
  errorMessage,
  timescale,
  onSelect,
  clearDateInput,
  dateId,
  startIn,
  endIn,
  deleteStart,
  clearSection = true,
  isDisabled,
  minDate,
  maxDate,
  styles,
  leftIcon,
  hideChevron,
  placeholderLabel,
  filledColor,
  emptyColor,
}: any) => {
  return (
    <FormControl
      sx={datePickerStyle}
      data-testid='datePicker-test'
      style={{ width: styles?.width ?? 'fit-content' }}
    >
      {label && <FormLabel>{label}</FormLabel>}

      <DatePicker
        data-testid='datePicker-test'
        selected={selected}
        onChange={onChange}
        disabled={isDisabled}
        placeholderText={placeholder}
        dateFormat={timescale === 'Y' ? 'yyyy' : timescale === 'M' ? 'MMM yyyy' : 'dd-MM-yyyy'}
        showMonthYearPicker={timescale === 'M'}
        showYearPicker={timescale === 'Y'}
        customInput={
          <ModelSettingsDatePickerInput
            planPermission={isDisabled}
            styles={styles}
            leftIcon={leftIcon}
            hideChevron={hideChevron}
            placeholderLabel={placeholderLabel}
            filledColor={filledColor}
            emptyColor={emptyColor}
          />
        }
        onSelect={onSelect}
        minDate={minDate}
        maxDate={maxDate}
      >
        {clearSection && (
          <Flex
            marginBottom='15px'
            borderTop='1px solid rgba(112, 138, 138, 0.3)'
            width='90%'
            display='flex'
            justifyContent='center'
            alignItems='center'
            paddingTop='10px'
            color='purple'
            onClick={() => {
              clearDateInput(dateId, deleteStart, startIn, endIn);
            }}
          >
            <Box>Clear Selection</Box>
          </Flex>
        )}
      </DatePicker>

      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </FormControl>
  );
};

export default ModelSettingsDatePicker;
